/**
 * KI-Anbieter-Sektion (ADR 0021, DPA-015) für einen einzelnen Mandanten.
 * Wird in TenantDetailPage eingehängt (Muster: LicenseSection).
 *
 *   - GET /tenants/:slug/ai-settings  → gespeicherter + wirksamer Modus
 *   - PUT /tenants/:slug/ai-settings  → { providerMode, reason }
 *
 * local_only ist die Vorgabe (auch ohne gespeicherte Zeile). allow_remote
 * (EU-LLM bei IONOS, nur Datenklasse intern) ist nur wählbar, wenn das
 * Deployment es freigibt (PLATFORM_ADMIN_AI_REMOTE_ALLOWED); sonst bleibt die
 * Option gesperrt mit Hinweis auf DPA-015. Der Wechsel auf allow_remote
 * verlangt eine Begründung und die getippte Bestätigung (Phrase = Slug).
 *
 * Zweisprachig: Deutsch zuerst, Englisch dahinter. Der Client hat keine
 * i18n-Bibliothek, deshalb die kleine {de, en}-Tabelle AI_SETTINGS_TEXT.
 *
 * Aufgeteilt in AiSettingsSection (lädt/speichert) und AiSettingsForm (reine
 * Darstellung), damit die Darstellung ohne DOM testbar ist
 * (AiSettingsSection.test.tsx rendert sie mit react-dom/server).
 */
import { useEffect, useState } from 'react';
import {
  AiProviderMode,
  TenantAiSettings,
  extractApiError,
  getTenantAiSettings,
  updateTenantAiSettings,
} from '../services/api';
import { TypedConfirmModal } from './TypedConfirmModal';

export interface Bilingual {
  de: string;
  en: string;
}

/** Gleiche Grenzen wie tenant-ai-settings.routes.ts (zod). */
export const AI_REASON_MAX = 500;
export const AI_REASON_MIN_REMOTE = 3;

export const AI_SETTINGS_TEXT = {
  title: { de: 'KI-Anbieter', en: 'AI provider' },
  intro: {
    de: 'Legt fest, ob der Copilot dieses Mandanten neben den eigenen Modellen ein EU-LLM nutzen darf.',
    en: 'Controls whether this tenant\'s copilot may use an EU LLM in addition to the platform\'s own models.',
  },
  localOnly: { de: 'Nur lokal (Standard)', en: 'Local only (default)' },
  allowRemote: { de: 'EU-LLM erlauben (IONOS, nur Datenklasse intern)', en: 'Allow EU LLM (IONOS, internal data only)' },
  remoteUnavailable: { de: 'In diesem Deployment nicht freigegeben (DPA-015)', en: 'Not enabled for this deployment' },
  reason: { de: 'Begründung', en: 'Reason' },
  reasonRequired: {
    de: `Für „EU-LLM erlauben“ ist eine Begründung Pflicht (mind. ${AI_REASON_MIN_REMOTE} Zeichen).`,
    en: `A reason is required to allow the EU LLM (at least ${AI_REASON_MIN_REMOTE} characters).`,
  },
  reasonTooLong: {
    de: `Begründung höchstens ${AI_REASON_MAX} Zeichen.`,
    en: `Reason must be at most ${AI_REASON_MAX} characters.`,
  },
  effective: { de: 'Wirksam', en: 'Effective' },
  stored: { de: 'Gespeichert', en: 'Stored' },
  storedNotEffective: {
    de: 'Gespeichert, in diesem Deployment aber nicht wirksam – es gilt „Nur lokal“.',
    en: 'Stored, but not in effect for this deployment – local only applies.',
  },
  lastChange: { de: 'Zuletzt geändert', en: 'Last changed' },
  neverChanged: { de: 'Noch nie geändert (Vorgabe)', en: 'Never changed (default)' },
  copilotNote: {
    de: 'Hinweis: user-copilot führt Werkzeug-Turns in dieser Version unabhängig von dieser Einstellung lokal aus (v1); er liest sie und weist sie im Audit aus.',
    en: 'Note: user-copilot currently keeps tool turns local regardless of this setting (v1); it reads the setting and reports it in its audit.',
  },
  readOnly: {
    de: 'Read-only-Operator; der KI-Anbieter kann nicht geändert werden.',
    en: 'Read-only operator; the AI provider cannot be changed.',
  },
  save: { de: 'Speichern', en: 'Save' },
  loading: { de: 'Lade KI-Anbieter…', en: 'Loading AI provider…' },
  loadFailed: { de: 'KI-Anbieter konnte nicht geladen werden', en: 'Could not load the AI provider' },
  confirmTitle: { de: 'EU-LLM erlauben?', en: 'Allow EU LLM?' },
  confirmDescription: {
    de: 'Der Copilot dieses Mandanten darf danach Daten der Klasse „intern“ an das EU-LLM (IONOS) senden. Die Begründung landet im Audit.',
    en: 'This tenant\'s copilot may then send data classified "internal" to the EU LLM (IONOS). The reason is recorded in the audit log.',
  },
  confirmLabel: { de: 'EU-LLM erlauben', en: 'Allow EU LLM' },
} satisfies Record<string, Bilingual>;

/** Ein Text als einzeiliger String „Deutsch / English“ (für Attribute und Modal-Props). */
export function bi(t: Bilingual): string {
  return `${t.de} / ${t.en}`;
}

function Bi({ t }: { t: Bilingual }) {
  return (
    <>
      {t.de} <span className="text-slate-400">/ {t.en}</span>
    </>
  );
}

export function modeLabel(mode: AiProviderMode): Bilingual {
  return mode === 'allow_remote' ? AI_SETTINGS_TEXT.allowRemote : AI_SETTINGS_TEXT.localOnly;
}

/** allow_remote nur mit Deployment-Freigabe und nur für Operatoren mit Schreibrecht. */
export function canSelectRemote(remoteAllowed: boolean, isReadOnly: boolean): boolean {
  return remoteAllowed && !isReadOnly;
}

/** Clientseitige Prüfung, dieselben Regeln wie die Route. null = in Ordnung. */
export function aiSettingsProblem(mode: AiProviderMode, reason: string): Bilingual | null {
  const r = reason.trim();
  if (r.length > AI_REASON_MAX) return AI_SETTINGS_TEXT.reasonTooLong;
  if (mode === 'allow_remote' && r.length < AI_REASON_MIN_REMOTE) return AI_SETTINGS_TEXT.reasonRequired;
  return null;
}

/** Getippte Bestätigung nur beim WECHSEL auf allow_remote, nicht beim Ändern der Begründung. */
export function needsTypedConfirm(stored: AiProviderMode, next: AiProviderMode): boolean {
  return next === 'allow_remote' && stored !== 'allow_remote';
}

export function isDirty(settings: TenantAiSettings, mode: AiProviderMode, reason: string): boolean {
  return mode !== settings.storedMode || reason.trim() !== (settings.reason ?? '');
}

export function canSave(
  settings: TenantAiSettings,
  mode: AiProviderMode,
  reason: string,
  isReadOnly: boolean,
  busy: boolean,
): boolean {
  if (isReadOnly || busy) return false;
  if (!isDirty(settings, mode, reason)) return false;
  if (aiSettingsProblem(mode, reason)) return false;
  return mode !== 'allow_remote' || settings.remoteAllowed;
}

/** Fehler der API als zweisprachiger Text; 409 ai_remote_unavailable bekommt den DPA-Hinweis. */
export function describeSaveError(err: unknown): string {
  const e = err as { response?: { data?: { code?: string; error?: string; issues?: Array<{ message?: string }> } } };
  const data = e.response?.data;
  if (data?.code === 'ai_remote_unavailable') return bi(AI_SETTINGS_TEXT.remoteUnavailable);
  if (data?.error === 'ValidationError' && data.issues?.[0]?.message) return data.issues[0].message;
  return extractApiError(err);
}

export interface AiSettingsFormProps {
  slug: string;
  settings: TenantAiSettings;
  mode: AiProviderMode;
  reason: string;
  isReadOnly: boolean;
  busy: boolean;
  err: string | null;
  onModeChange: (mode: AiProviderMode) => void;
  onReasonChange: (reason: string) => void;
  onSave: () => void;
}

export function AiSettingsForm({
  slug,
  settings,
  mode,
  reason,
  isReadOnly,
  busy,
  err,
  onModeChange,
  onReasonChange,
  onSave,
}: AiSettingsFormProps) {
  const remoteSelectable = canSelectRemote(settings.remoteAllowed, isReadOnly);
  const problem = isDirty(settings, mode, reason) ? aiSettingsProblem(mode, reason) : null;
  const storedDiffers = settings.storedMode !== settings.providerMode;
  const T = AI_SETTINGS_TEXT;

  return (
    <section className="mb-6" data-testid="ai-settings">
      <h3 className="font-semibold mb-2"><Bi t={T.title} /></h3>
      <p className="text-xs text-slate-500 mb-3"><Bi t={T.intro} /></p>

      {err && <div className="bg-red-100 text-red-700 p-3 rounded mb-3 text-sm" role="alert">{err}</div>}

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded shadow p-4 space-y-3">
          <fieldset className="space-y-2" disabled={isReadOnly || busy}>
            <legend className="sr-only">{bi(T.title)}</legend>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name={`ai-mode-${slug}`}
                value="local_only"
                checked={mode === 'local_only'}
                onChange={() => onModeChange('local_only')}
                disabled={isReadOnly || busy}
              />
              <span><Bi t={T.localOnly} /></span>
            </label>
            <label className={`flex items-start gap-2 text-sm ${remoteSelectable ? '' : 'text-slate-400'}`}>
              <input
                type="radio"
                name={`ai-mode-${slug}`}
                value="allow_remote"
                checked={mode === 'allow_remote'}
                onChange={() => onModeChange('allow_remote')}
                disabled={!remoteSelectable || busy}
              />
              <span>
                <Bi t={T.allowRemote} />
                {!settings.remoteAllowed && (
                  <span className="block text-xs text-amber-700" data-testid="ai-remote-unavailable">
                    <Bi t={T.remoteUnavailable} />
                  </span>
                )}
              </span>
            </label>
          </fieldset>

          <div>
            <label htmlFor={`ai-reason-${slug}`} className="block text-sm text-slate-600 mb-1">
              <Bi t={T.reason} />{mode === 'allow_remote' && <span className="text-red-600"> *</span>}
            </label>
            <textarea
              id={`ai-reason-${slug}`}
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              disabled={isReadOnly || busy}
              maxLength={AI_REASON_MAX}
              rows={2}
              className="w-full px-2 py-1 rounded border text-sm"
            />
            {problem && <p className="text-xs text-red-600 mt-1"><Bi t={problem} /></p>}
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={onSave}
              disabled={!canSave(settings, mode, reason, isReadOnly, busy)}
              className="btn-primary disabled:opacity-50"
            >
              {bi(T.save)}
            </button>
          </div>
          {isReadOnly && <p className="text-xs text-slate-400"><Bi t={T.readOnly} /></p>}
        </div>

        <div className="space-y-2">
          <div className="bg-white rounded shadow p-4 text-sm space-y-1">
            <div>
              <Bi t={T.effective} />:{' '}
              <span className="badge bg-slate-200" data-testid="ai-effective-mode">{bi(modeLabel(settings.providerMode))}</span>
            </div>
            {storedDiffers && (
              <div className="text-amber-700" data-testid="ai-stored-not-effective">
                <Bi t={T.stored} />: {bi(modeLabel(settings.storedMode))} — <Bi t={T.storedNotEffective} />
              </div>
            )}
            <div className="text-slate-600">
              {settings.updatedAt ? (
                <>
                  <Bi t={T.lastChange} />:{' '}
                  <span className="font-mono">{new Date(settings.updatedAt).toLocaleString()}</span>
                  {settings.updatedByEmail || settings.updatedBy ? (
                    <> · <span className="font-mono">{settings.updatedByEmail || settings.updatedBy}</span></>
                  ) : null}
                </>
              ) : (
                <Bi t={T.neverChanged} />
              )}
            </div>
            {settings.reason && (
              <div className="text-slate-600"><Bi t={T.reason} />: {settings.reason}</div>
            )}
          </div>
          <p className="text-xs text-slate-500" data-testid="ai-copilot-note"><Bi t={T.copilotNote} /></p>
        </div>
      </div>
    </section>
  );
}

interface Props {
  slug: string;
  isReadOnly: boolean;
}

export function AiSettingsSection({ slug, isReadOnly }: Props) {
  const [loaded, setLoaded] = useState(false);
  const [settings, setSettings] = useState<TenantAiSettings | null>(null);
  const [mode, setMode] = useState<AiProviderMode>('local_only');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  function apply(s: TenantAiSettings): void {
    setSettings(s);
    setMode(s.storedMode);
    setReason(s.reason ?? '');
  }

  useEffect(() => {
    let cancelled = false;
    getTenantAiSettings(slug).then((s) => {
      if (cancelled) return;
      apply(s);
      setLoaded(true);
    }).catch(() => {
      if (!cancelled) { setErr(bi(AI_SETTINGS_TEXT.loadFailed)); setLoaded(true); }
    });
    return () => { cancelled = true; };
  }, [slug]);

  async function save(): Promise<void> {
    if (isReadOnly || !settings) return;
    setConfirming(false);
    setBusy(true);
    setErr(null);
    try {
      apply(await updateTenantAiSettings(slug, { providerMode: mode, reason: reason.trim() }));
    } catch (e) {
      setErr(describeSaveError(e));
    } finally {
      setBusy(false);
    }
  }

  function requestSave(): void {
    if (!settings) return;
    const problem = aiSettingsProblem(mode, reason);
    if (problem) { setErr(bi(problem)); return; }
    if (needsTypedConfirm(settings.storedMode, mode)) { setConfirming(true); return; }
    void save();
  }

  if (!loaded) return <p className="text-slate-500 text-sm">{bi(AI_SETTINGS_TEXT.loading)}</p>;
  if (!settings) {
    return (
      <section className="mb-6">
        <h3 className="font-semibold mb-2"><Bi t={AI_SETTINGS_TEXT.title} /></h3>
        <div className="bg-red-100 text-red-700 p-3 rounded text-sm">{err ?? bi(AI_SETTINGS_TEXT.loadFailed)}</div>
      </section>
    );
  }

  return (
    <>
      <AiSettingsForm
        slug={slug}
        settings={settings}
        mode={mode}
        reason={reason}
        isReadOnly={isReadOnly}
        busy={busy}
        err={err}
        onModeChange={(m) => { setMode(m); setErr(null); }}
        onReasonChange={(r) => { setReason(r); setErr(null); }}
        onSave={requestSave}
      />
      {confirming && (
        <TypedConfirmModal
          title={bi(AI_SETTINGS_TEXT.confirmTitle)}
          description={bi(AI_SETTINGS_TEXT.confirmDescription)}
          confirmPhrase={slug}
          confirmLabel={bi(AI_SETTINGS_TEXT.confirmLabel)}
          affectedSlugs={[slug]}
          onConfirm={() => { void save(); }}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
}
